import { FormattedMessage } from 'react-intl';
import { Link, NavLink } from 'react-router';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import IframeResizer from 'iframe-resizer-react';

import { config } from '../../config';
import { routes } from '../../routes/routes';
import { useAppSelector } from '../../configuration/setup/hooks.ts';
import { getIdToken } from '../../configuration/tokenHandling/tokenSlice.ts';
import { useEffect, useState } from 'react';

const getMenuOrigin = (menuUrl: string | undefined) => {
    if (!menuUrl) {
        return undefined;
    }

    try {
        return new URL(menuUrl).origin;
    } catch {
        return undefined;
    }
};

const navItems = [
    {
        key: 'accounts',
        route: (
            <NavLink to={routes.DEFAULT}>
                <FormattedMessage id='intl-msg:helpgang.sublink.accounts' />
            </NavLink>
        ),
    },
    {
        key: 'partner_integrations',
        route: (
            <NavLink to={routes.PARTNER_INTEGRATIONS}>
                <FormattedMessage id='intl-msg:helpgang.sublink.partner_integrations' />
            </NavLink>
        ),
    },
    {
        key: 'client_metrics',
        route: (
            <NavLink to={routes.CLIENT_METRICS}>
                <FormattedMessage id='intl-msg:helpgang.sublink.client_metrics' />
            </NavLink>
        ),
    },
];

const AppHeader = () => {
    const [appMenuOpen, setAppMenuOpen] = useState(false);
    const [appMenuResetKey, setAppMenuResetKey] = useState(0);

    const menuOrigin = getMenuOrigin(config.menuUrl);

    // biome-ignore lint/style/noNonNullAssertion: trust
    const idToken = useAppSelector(getIdToken)!;

    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;

    const userMenuItem = (
        <DefaultUserMenu environment={import.meta.env.MODE} disableCookieModal={true} postLogout={postLogout} />
    );

    const closeAppMenu = () => {
        if (!appMenuOpen) {
            return;
        }

        document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        setAppMenuResetKey(current => current + 1);
        setAppMenuOpen(false);
    };

    useEffect(() => {
        const handleMenuMessage = (event: MessageEvent) => {
            if (event.data?.type !== 'MENU_LINK_CLICKED') {
                return;
            }

            if (menuOrigin && event.origin !== menuOrigin) {
                return;
            }

            closeAppMenu();

            const targetWindow = event.source as WindowProxy | null;
            if (targetWindow) {
                targetWindow.postMessage({ type: 'MENU_LINK_HANDLED', requestId: event.data.requestId }, event.origin);
            }
        };

        window.addEventListener('message', handleMenuMessage);

        return () => {
            window.removeEventListener('message', handleMenuMessage);
        };
    }, [appMenuOpen, menuOrigin]);

    return (
        <ApplicationHeader
            key={appMenuResetKey}
            label={<FormattedMessage id='intl-msg:helpgang.moduleName' />}
            appNavigator={<IframeResizer className='iFrameResizer' src={config.menuUrl} />}
            homeRoute={<Link to={config.homeRoute || ''} />}
            navItems={navItems}
            actionBarItems={[userMenuItem]}
            onToggleAppMenu={setAppMenuOpen}
        />
    );
};

export default AppHeader;
